import { Switch } from '@/components/ui/switch';
import { updateProgram } from '@/services/affiliate-programs-service';
import type { Program } from '@fluenceur/types';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

interface ProgramDeepLinkingProps {
  programId: string;
  enabled: boolean;
}

/**
 * Toggle controlling whether affiliates may create tracked deep links to
 * specific product pages. Co-located with ProgramStatus so each interactive
 * control on the details card stays a single-purpose component.
 */
const ProgramDeepLinking: React.FC<ProgramDeepLinkingProps> = ({ programId, enabled }) => {
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(enabled);
  const [isSaving, setIsSaving] = useState(false);

  // Sync internal state when navigating between programs or when prop changes
  useEffect(() => {
    setChecked(enabled);
  }, [programId, enabled]);

  const handleToggle = async (nextValue: boolean) => {
    const previous = checked;
    setChecked(nextValue);
    setIsSaving(true);
    try {
      await updateProgram(programId, { deepLinkingEnabled: nextValue });

      // Optimistically update cached detail and list queries
      queryClient.setQueryData<Program>(['program', programId], (old) =>
        old ? { ...old, deepLinkingEnabled: nextValue } : old
      );
      queryClient.setQueryData<Program[]>(
        ['programs'],
        (old) =>
          old?.map((p) => (p.id === programId ? { ...p, deepLinkingEnabled: nextValue } : p)) ?? old
      );

      // Ensure any subscribers refetch if needed
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
    } catch (error) {
      console.error('Error while updating program deep linking:', error);
      setChecked(previous);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Switch
      checked={checked}
      disabled={isSaving}
      colorPalette="primary"
      onCheckedChange={(details) => handleToggle(details.checked)}
    />
  );
};

export default ProgramDeepLinking;
